import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { api, fmtEur } from "../api";
import { useLang } from "../i18n";
import { Scale, TrendingUp, Clock, Repeat, ShieldCheck, ArrowRight, CheckCircle2, Users } from "lucide-react";

const T = {
  it: {
    kicker: "Metodologia",
    title: "Algoritmo non discrezionale. Validazione di un team di analisti.",
    intro: "Ogni sera, alla chiusura dei mercati, un algoritmo analizza il Bund Future e calcola l'indicazione operativa per l'apertura successiva. Ma il sistema non è lasciato solo: il team di BundStrategy ne valida costantemente i risultati alla luce dei dati macroeconomici e degli eventi finanziari attuali. È questa combinazione — analisi algoritmica non discrezionale più analisi ponderata economico-finanziaria — a rendere il sistema profittevole.",
    engine1_title: "Motore 1 — Mean Reversion",
    engine1_text: "Il Bund passa la maggior parte del tempo in fasi laterali. Il sistema misura quanto il prezzo si è allontanato dalla sua media delle ultime 25 sedute (z-score). Quando l'allontanamento supera la soglia statistica di ±2.0 — un evento raro — apre una posizione contraria attendendo il rientro verso la media.",
    engine2_title: "Motore 2 — Trend macro-confermato",
    engine2_text: "Quando il mercato esce dal range e sviluppa un movimento superiore a 4 punti in 90 sedute, il sistema verifica che la direzione sia confermata dal momentum del T-Note USA a 10 anni (i tassi globali si muovono insieme). Solo con doppia conferma il sistema abbandona la mean reversion e segue il trend: è ciò che ha catturato il grande ribasso del 2022.",
    team_title: "Il terzo pilastro — la validazione del team",
    team_text: "Il sistema opera costantemente dietro la validazione del team di BundStrategy. I dati generati dall'algoritmo vengono valutati e analizzati ogni giorno alla luce del quadro macroeconomico e degli eventi finanziari attuali: decisioni delle banche centrali, dati su inflazione e crescita, aste e spread governativi, eventi geopolitici. L'algoritmo garantisce disciplina e assenza di emotività; gli analisti tecnici ed economici garantiscono il contesto. È la combinazione dei due che fa la differenza.",
    timeline_title: "Come nasce il segnale, ogni sera",
    steps: [
      ["22:00", "Chiusura dei mercati", "Il dato ufficiale End-Of-Day del Bund viene acquisito e validato automaticamente."],
      ["22:15", "Calcolo automatico", "L'engine elabora z-score, trend a 90 sedute e conferma macro del T-Note USA."],
      ["22:30", "Validazione e pubblicazione", "Il team valida l'indicazione alla luce del contesto macro e degli eventi in corso; l'indicazione per l'apertura del giorno dopo è visibile agli abbonati: LONG, SHORT, S&R, HOLD o FLAT, con motivazione sintetica."],
      ["08:00", "Esecuzione all'apertura", "L'operazione è registrata al prezzo di apertura ufficiale. Tutto contabilizzato e pubblico."],
    ],
    rules_title: "Regole operative fisse",
    rules: [
      "Sempre a mercato con 1 solo contratto Bund Future standard",
      "Stop & reverse: alla chiusura di una posizione, apertura contestuale opposta",
      "Rollover automatico sul contratto trimestrale successivo (Mar/Giu/Set/Dic)",
      "Algoritmo non discrezionale, validato dal team di analisti BundStrategy: ogni intervento sul segnale è motivato dal contesto economico-finanziario e tracciato in un log",
    ],
    numbers_title: "I numeri, verificabili riga per riga",
    numbers_text: "Tutte le statistiche derivano dalle operazioni registrate, consultabili nello storico. Le performance sono riferite a 1 contratto standard; le percentuali sono calcolate ipotizzando un capitale iniziale di 5.000 € (circa 2.700-3.000 € di margine operativo + riserva per eventuali drawdown iniziali).",
    stat_total: "P/L totale dal 2022", stat_wr: "Win-rate reale", stat_dd: "Max drawdown", stat_years: "Anni positivi",
    transparency_title: "Trasparenza e limiti",
    transparency: [
      "I risultati storici includono backtest su dati reali: le performance passate non garantiscono risultati futuri",
      "Operatività basata su dati End-Of-Day ufficiali, con validazione automatica dei prezzi",
      "Lo storico completo di segnali, operazioni e report è sempre consultabile e scaricabile",
      "Questo servizio pubblica segnali e traccia performance: non invia ordini e non è consulenza finanziaria personalizzata",
    ],
    cta_title: "Guarda il sistema all'opera",
    cta_text: "Prova gratis per un mese con dati completi (ritardo di 1 giorno), oppure abbonati per ricevere il segnale in tempo reale ogni sera alle 22:30.",
    cta_trial: "Attiva il trial gratuito", cta_plans: "Vedi i piani",
  },
  en: {
    kicker: "Methodology",
    title: "Non-discretionary algorithm. Validated by a team of analysts.",
    intro: "Every evening at market close, an algorithm analyses the Bund Future and computes the operational indication for the next open. But the system is never left alone: the BundStrategy team constantly validates its output against macroeconomic data and current financial events. It is this combination — non-discretionary algorithmic analysis plus weighted economic-financial analysis — that makes the system profitable.",
    engine1_title: "Engine 1 — Mean Reversion",
    engine1_text: "The Bund spends most of its time in sideways phases. The system measures how far price has stretched from its 25-session mean (z-score). When the stretch exceeds the statistical threshold of ±2.0 — a rare event — it opens a contrarian position expecting reversion to the mean.",
    engine2_title: "Engine 2 — Macro-confirmed trend",
    engine2_text: "When the market leaves the range and develops a move larger than 4 points over 90 sessions, the system checks that the direction is confirmed by the momentum of the US 10Y T-Note (global rates move together). Only with double confirmation does the system abandon mean reversion and follow the trend: this is what captured the great 2022 sell-off.",
    team_title: "The third pillar — team validation",
    team_text: "The system constantly operates under the validation of the BundStrategy team. The data generated by the algorithm is assessed and analysed every day in light of the macroeconomic picture and current financial events: central bank decisions, inflation and growth data, government auctions and spreads, geopolitical events. The algorithm guarantees discipline and absence of emotion; the technical and economic analysts guarantee context. It is the combination of the two that makes the difference.",
    timeline_title: "How the signal is born, every evening",
    steps: [
      ["22:00", "Market close", "The official Bund End-Of-Day data is acquired and validated automatically."],
      ["22:15", "Automatic computation", "The engine processes z-score, 90-session trend and US T-Note macro confirmation."],
      ["22:30", "Validation and publication", "The team validates the indication against the macro context and ongoing events; the indication for tomorrow's open is visible to subscribers: LONG, SHORT, S&R, HOLD or FLAT, with a concise motivation."],
      ["08:00", "Execution at the open", "The operation is recorded at the official opening price. Everything accounted and public."],
    ],
    rules_title: "Fixed operating rules",
    rules: [
      "Always in the market with 1 standard Bund Future contract",
      "Stop & reverse: closing a position opens the opposite one at the same time",
      "Automatic rollover to the next quarterly contract (Mar/Jun/Sep/Dec)",
      "Non-discretionary algorithm, validated by the BundStrategy team of analysts: any intervention on the signal is motivated by the economic-financial context and tracked in a log",
    ],
    numbers_title: "Numbers you can verify line by line",
    numbers_text: "All statistics derive from recorded operations, available in the history. Performance refers to 1 standard contract; percentages are calculated assuming an initial capital of €5,000 (about €2,700-3,000 trading margin + reserve for potential initial drawdowns).",
    stat_total: "Total P/L since 2022", stat_wr: "Real win-rate", stat_dd: "Max drawdown", stat_years: "Positive years",
    transparency_title: "Transparency and limits",
    transparency: [
      "Historical results include backtests on real data: past performance does not guarantee future results",
      "Operations based on official End-Of-Day data, with automatic price validation",
      "The full history of signals, operations and reports is always available and downloadable",
      "This service publishes signals and tracks performance: it does not send orders and is not personalized financial advice",
    ],
    cta_title: "Watch the system at work",
    cta_text: "Try it free for a month with full data (1-day delay), or subscribe to receive the real-time signal every evening at 22:30.",
    cta_trial: "Activate free trial", cta_plans: "See plans",
  },
};

export default function Methodology() {
  const { lang } = useLang();
  const c = T[lang] || T.it;
  const [stats, setStats] = useState(null);
  const [years, setYears] = useState([]);

  useEffect(() => {
    api.get("/public/performance").then((r) => setStats(r.data)).catch(() => {});
    api.get("/public/performance/yearly").then((r) => setYears(r.data)).catch(() => {});
  }, []);

  const positiveYears = years.filter((y) => y.pl_eur > 0).length;

  return (
    <div data-testid="methodology-page" className="max-w-5xl mx-auto px-4 sm:px-6 py-16">
      <p className="text-xs tracking-[0.3em] uppercase text-emerald-400 font-mono">{c.kicker}</p>
      <h1 className="font-heading text-4xl sm:text-5xl font-extrabold tracking-tighter text-white mt-3 max-w-3xl leading-[1.05]">{c.title}</h1>
      <p className="text-slate-300 mt-6 max-w-2xl leading-relaxed">{c.intro}</p>

      <div className="grid md:grid-cols-2 gap-6 mt-14">
        <div data-testid="method-engine-1" className="border border-emerald-500/25 bg-emerald-500/5 p-7">
          <Scale className="w-7 h-7 text-emerald-400 mb-4" />
          <h2 className="font-heading text-lg font-bold text-white">{c.engine1_title}</h2>
          <p className="text-sm text-slate-300 mt-3 leading-relaxed">{c.engine1_text}</p>
        </div>
        <div data-testid="method-engine-2" className="border border-amber-500/25 bg-amber-500/5 p-7">
          <TrendingUp className="w-7 h-7 text-amber-400 mb-4" />
          <h2 className="font-heading text-lg font-bold text-white">{c.engine2_title}</h2>
          <p className="text-sm text-slate-300 mt-3 leading-relaxed">{c.engine2_text}</p>
        </div>
      </div>

      <div data-testid="method-team" className="border border-sky-500/25 bg-sky-500/5 p-7 sm:p-9 mt-6">
        <Users className="w-7 h-7 text-sky-400 mb-4" />
        <h2 className="font-heading text-lg font-bold text-white">{c.team_title}</h2>
        <p className="text-sm text-slate-300 mt-3 leading-relaxed max-w-3xl">{c.team_text}</p>
      </div>

      <h2 className="font-heading text-2xl font-bold text-white mt-16 flex items-center gap-3">
        <Clock className="w-5 h-5 text-emerald-400" /> {c.timeline_title}
      </h2>
      <div data-testid="method-timeline" className="mt-6 border-l border-white/15 ml-2 space-y-8">
        {c.steps.map(([time, title, text], i) => (
          <div key={i} className="relative pl-8">
            <span className="absolute -left-[5px] top-1.5 w-2.5 h-2.5 rounded-full bg-emerald-400" />
            <p className="font-mono text-emerald-400 text-sm">{time} CET</p>
            <p className="text-white font-medium mt-0.5">{title}</p>
            <p className="text-sm text-slate-400 mt-1 max-w-xl leading-relaxed">{text}</p>
          </div>
        ))}
      </div>

      <h2 className="font-heading text-2xl font-bold text-white mt-16 flex items-center gap-3">
        <Repeat className="w-5 h-5 text-emerald-400" /> {c.rules_title}
      </h2>
      <ul data-testid="method-rules" className="mt-5 grid sm:grid-cols-2 gap-3">
        {c.rules.map((r, i) => (
          <li key={i} className="flex items-start gap-2.5 border border-white/10 bg-[#111111] p-4 text-sm text-slate-300 leading-relaxed">
            <CheckCircle2 className="w-4 h-4 text-emerald-400 mt-0.5 shrink-0" /> {r}
          </li>
        ))}
      </ul>

      <h2 className="font-heading text-2xl font-bold text-white mt-16">{c.numbers_title}</h2>
      <p className="text-sm text-slate-400 mt-2 max-w-2xl">{c.numbers_text}</p>
      {stats && (
        <div data-testid="method-stats" className="grid grid-cols-2 md:grid-cols-4 border border-white/10 divide-x divide-y divide-white/10 md:divide-y-0 mt-6">
          {[
            [c.stat_total, fmtEur(stats.total_eur, lang), stats.total_eur >= 0],
            [c.stat_wr, `${stats.win_rate}%`, true],
            [c.stat_dd, fmtEur(-stats.max_drawdown_eur, lang), false],
            [c.stat_years, `${positiveYears} / ${years.length}`, true],
          ].map(([label, value, pos], i) => (
            <div key={i} className="p-5 bg-[#111111]">
              <p className="text-[10px] tracking-[0.2em] uppercase text-slate-500">{label}</p>
              <p className={`font-mono text-xl mt-1 ${pos ? "text-emerald-400" : "text-red-400"}`}>{value}</p>
            </div>
          ))}
        </div>
      )}

      <h2 className="font-heading text-2xl font-bold text-white mt-16 flex items-center gap-3">
        <ShieldCheck className="w-5 h-5 text-emerald-400" /> {c.transparency_title}
      </h2>
      <ul data-testid="method-transparency" className="mt-5 space-y-2.5">
        {c.transparency.map((r, i) => (
          <li key={i} className="text-sm text-slate-400 leading-relaxed pl-4 border-l-2 border-white/15">{r}</li>
        ))}
      </ul>

      <div data-testid="method-cta" className="mt-16 border border-emerald-500/30 bg-emerald-500/5 p-8 sm:p-10">
        <h2 className="font-heading text-2xl font-bold text-white">{c.cta_title}</h2>
        <p className="text-slate-300 mt-2 max-w-xl">{c.cta_text}</p>
        <div className="flex flex-wrap gap-4 mt-6">
          <Link to="/pricing" data-testid="method-cta-trial" className="inline-flex items-center gap-2 bg-emerald-500 hover:bg-emerald-400 text-black font-medium px-6 py-3 text-sm transition-colors">
            {c.cta_trial} <ArrowRight className="w-4 h-4" />
          </Link>
          <Link to="/pricing" data-testid="method-cta-plans" className="inline-flex items-center gap-2 border border-white/20 text-white hover:bg-white/5 px-6 py-3 text-sm transition-colors">
            {c.cta_plans}
          </Link>
        </div>
      </div>
    </div>
  );
}
